import React, { memo, useCallback, useEffect, useState } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { Select } from "@pd/pd-ui-kit";
import { AxiosError } from "axios";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router-dom";

import Back from "~/components/Icons/arrow-prev2";
import { useGetEmptyUsersForSelect } from "~/entity/Employee";
import { EmployeeService } from "~/entity/Employee/api/service";
import { EmployeeCardEmployeeWithGaps } from "~/entity/Employee/lib/restGaps";
import { goBackToBaseStaff } from "~/pages/BaseStaff/lib/goBackToBaseStaff";
import { splitEmployeeFullName } from "~/pages/BaseStaff/model/baseStaffUrlParams";
import { useNotify } from "~/shared/hooks/useNotify";

import FormFooter from "./sections/FormFooter";
import ParametersSection from "./sections/ParametersSection";
import PersonalDataSection from "./sections/PersonalDataSection";
import PositionsSection from "./sections/PositionsSection";
import {
  getFirePositionsPayload,
  mapCardToForm,
  mapFormToCardUpdate,
  mapFormToCreate,
} from "./model/adapter";
import { getDefaultFormValues } from "./model/config";
import { normalizeFormDate } from "./model/normalizeFormDate";
import { IEmployeeProfileForm } from "./model/types";
import { useTrainingLicenses } from "./model/useTrainingLicenses";
import { getEmployeeProfileValidation } from "./model/validation";

import s from "./EmployeeProfileForm.less";

interface IEmployeeProfileFormProps {
  employee?: EmployeeCardEmployeeWithGaps;
  refetch?: () => void;
}

const getRestErrorMessage = (error: unknown, fallback: string) => {
  if (!(error instanceof AxiosError)) {
    return fallback;
  }

  const message = (error.response?.data as { message?: unknown } | undefined)
    ?.message;
  if (typeof message === "string" && message) {
    return message;
  }
  if (Array.isArray(message) && typeof message[0] === "string") {
    return message.join("; ");
  }
  if (message && typeof message === "object") {
    const nested = message as { message?: unknown; errors?: unknown };
    if (Array.isArray(nested.errors) && nested.errors.length) {
      const messages = nested.errors
        .map((item) =>
          typeof item === "string"
            ? item
            : (item as { message?: unknown })?.message,
        )
        .filter((item): item is string => typeof item === "string" && !!item);
      if (messages.length) {
        return messages.join("; ");
      }
    }
    if (typeof nested.message === "string" && nested.message) {
      return nested.message;
    }
  }

  return error.message || fallback;
};

const EmployeeProfileForm = ({
  employee,
  refetch,
}: IEmployeeProfileFormProps) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { id: routeId } = useParams<{ id: string }>();
  const isAdd = routeId === "add";
  const [pendingSendCheck, setPendingSendCheck] = useState(false);
  const [saving, setSaving] = useState(false);

  const { control, handleSubmit, reset, setValue, watch, setError, errors } =
    useForm<IEmployeeProfileForm>({
      defaultValues: getDefaultFormValues(),
      resolver: yupResolver(getEmployeeProfileValidation(t)),
    });

  const positions = watch("positions");
  const { getLicenseInfo, loaded: trainingLicensesLoaded } =
    useTrainingLicenses(positions, isAdd ? undefined : routeId);

  useEffect(() => {
    (positions || []).forEach((position, index) => {
      const info = getLicenseInfo(position);

      if (!info) {
        return;
      }

      if (
        position.licenseFreeCount !== info.freeCount ||
        position.licenseAssigned !== info.assigned
      ) {
        setValue(`positions.${index}.licenseFreeCount`, info.freeCount);
        setValue(`positions.${index}.licenseAssigned`, info.assigned);
      }
    });
  }, [positions, getLicenseInfo, setValue]);

  const {
    users: emptyUsers,
    options: emptyUserOptions,
    loading: emptyUsersLoading,
    onSearch: onSearchEmptyUsers,
    onScrollBottom: onScrollBottomEmptyUsers,
    onOpenChange: onOpenChangeEmptyUsers,
  } = useGetEmptyUsersForSelect(isAdd);

  const [pickedEmptyUser, setPickedEmptyUser] = useState<{
    option: { value: string; label: string };
    user: (typeof emptyUsers)[number];
  } | null>(null);

  const handlePickEmptyUser = useCallback(
    (option?: { value: string; label: string } | null) => {
      if (!option) {
        setPickedEmptyUser(null);
        reset(getDefaultFormValues());
        return;
      }

      const user = emptyUsers.find((item) => String(item.id) === option.value);
      if (!user) {
        return;
      }
      setPickedEmptyUser({ option, user });

      const fallback = splitEmployeeFullName(user.fullName || "");
      const surname = user.surname || fallback.surname || "";
      const name = user.name || fallback.name || "";
      const middleName = user.middleName || fallback.middleName || "";

      setValue("surname", surname, { shouldValidate: true });
      setValue("name", name, { shouldValidate: true });
      setValue("middleName", middleName);
      setValue("noMiddleName", !middleName);
      setValue("gender", user.gender === false ? "Female" : "Male");
      setValue("birthDay", normalizeFormDate(user.birthDay), {
        shouldValidate: true,
      });
      setValue("hiredDate", normalizeFormDate(user.hiredDate), {
        shouldValidate: true,
      });
      setValue("ladaAcademiaId", user.ladaAcademiaId || "", {
        shouldValidate: true,
      });
      setValue("workPhone", user.workPhone || user.personalPhone || "", {
        shouldValidate: true,
      });
      setValue("positions.0.dealers.0.email", user.email || "", {
        shouldValidate: true,
      });
    },
    [emptyUsers, setValue, reset],
  );

  useEffect(() => {
    if (!isAdd && employee) {
      reset(mapCardToForm(employee));
    }
  }, [employee, isAdd, reset]);

  const { notifySuccess, notifyError: notifyErrorContent } = useNotify();

  const notifyError = useCallback(
    (error: unknown) => {
      notifyErrorContent(
        getRestErrorMessage(error, t("errors.VALIDATION_ERROR")),
      );
    },
    [notifyErrorContent, t],
  );

  const editCard = useCallback(
    async (data: IEmployeeProfileForm, withSendCheck = false) => {
      if (!employee) {
        return;
      }

      const employeeId = String(employee.id ?? routeId);
      setSaving(true);
      try {
        let firePositionsPayload = getFirePositionsPayload(data, employee);
        let shouldFirePositions = false;
        if (firePositionsPayload.items.length) {
          const { data: card } = await EmployeeService.GET.card(employeeId);
          firePositionsPayload = getFirePositionsPayload(data, card.employee);
          shouldFirePositions =
            firePositionsPayload.items.length > 0 &&
            card.employee?.status !== "fired";
        }

        await EmployeeService.PUT.updateCard(
          employeeId,
          mapFormToCardUpdate(data, employee),
        );

        if (shouldFirePositions) {
          await EmployeeService.POST.firePositions(firePositionsPayload);
        }

        if (withSendCheck) {
          await EmployeeService.POST.sendCheck(employeeId);
        }

        notifySuccess(t("common.сhange_was_successful"));
        refetch?.();
        history.replace("/base/staff/");
      } catch (error) {
        setPendingSendCheck(false);
        notifyError(error);
      } finally {
        setSaving(false);
      }
    },
    [notifySuccess, employee, history, notifyError, refetch, routeId, t],
  );

  const createCard = useCallback(
    async (data: IEmployeeProfileForm) => {
      setSaving(true);
      try {
        if (pickedEmptyUser) {
          await EmployeeService.PUT.updateCard(
            String(pickedEmptyUser.user.id),
            mapFormToCardUpdate(data, pickedEmptyUser.user),
          );
        } else {
          await EmployeeService.POST.create(mapFormToCreate(data));
        }
        notifySuccess(t("common.adding_was_successful"));
        history.replace("/base/staff/");
      } catch (error) {
        notifyError(error);
      } finally {
        setSaving(false);
      }
    },
    [notifySuccess, history, notifyError, pickedEmptyUser, t],
  );

  const handleRequestLicense = useCallback(() => {
    history.push("/staff/licenses/requests");
    window.scrollTo(0, 0);
  }, [history]);

  const onSave = useCallback(
    (data: IEmployeeProfileForm) => {
      if (isAdd) {
        createCard(data);
        return;
      }
      editCard(data);
    },
    [createCard, editCard, isAdd],
  );

  const onSendCheck = handleSubmit((data) => {
    if (!data.photoId) {
      setError("photoId", {
        type: "required",
        message: t("validation.required"),
      });
      return;
    }

    setPendingSendCheck(true);
    editCard(data, true);
  });

  const isSubmitting = saving;

  return (
    <div className={s.wrapper}>
      <div className={s.pageTop}>
        <button
          type="button"
          className={s.backButton}
          onClick={() => goBackToBaseStaff(history)}
        >
          <Back width="7" height="11" />
          <span>{t("ui.back")}</span>
        </button>
        <div className={s.titleRow}>
          <h1 className={s.title}>
            {isAdd ? t("employee.titleCreate") : t("employee.titleEmploy")}
          </h1>
          {isAdd && (
            <div className={s.addFromUsers}>
              <Select
                labelInValue
                showSearch
                allowClear
                value={pickedEmptyUser?.option ?? null}
                options={emptyUserOptions}
                loading={emptyUsersLoading}
                filterOption={false}
                placeholder={t("employee.addFromUsers")}
                onChange={handlePickEmptyUser}
                onSearch={onSearchEmptyUsers}
                onOpenChange={onOpenChangeEmptyUsers}
                onPopupScroll={(event) => {
                  const target = event.target as HTMLDivElement;
                  if (
                    target.scrollTop + target.offsetHeight >=
                    target.scrollHeight - 20
                  ) {
                    onScrollBottomEmptyUsers();
                  }
                }}
              />
            </div>
          )}
        </div>
      </div>

      <PersonalDataSection
        control={control}
        errors={errors}
        setValue={setValue}
        photo={watch("photo")}
      />

      <hr className={s.sectionDivider} />

      <ParametersSection control={control} errors={errors} />

      <hr className={s.sectionDivider} />

      <PositionsSection
        control={control}
        errors={errors}
        setValue={setValue}
        watch={watch}
        licensesLoaded={trainingLicensesLoaded}
        onRequestLicense={handleRequestLicense}
        refetch={refetch}
      />

      <FormFooter
        positions={positions}
        licensesLoaded={trainingLicensesLoaded}
        isAdd={isAdd}
        isSubmitting={isSubmitting}
        pendingSendCheck={pendingSendCheck}
        onSave={handleSubmit(onSave)}
        onSendCheck={onSendCheck}
        onRequestLicense={handleRequestLicense}
      />
    </div>
  );
};

export default memo(EmployeeProfileForm);
